// Used by: QuoteQueryJets, PortalQuoteQueryJets
import { useState, useEffect, useRef } from "react";
import useClickOutside from "../../hooks/useClickOutside";
import getClassName from "../../utilities/getClassName";
import chevronDown from "../../assets/icons/chevron-down-big.svg";
import chevronUp from "../../assets/icons/chevron-up-big.svg";

/**
 * Luggage selector with separate carry-on and hold bag counts.
 * Calls `value({ bags, carryOn })` whenever either count changes.
 */
const LuggageDropdown = ({
    id,
    icon,
    defaultValue = 0,
    defaultCarryOn = 1,
    value,
    min = 0,
    max = 99,
    validateForm,
    onFocus,
    skeleton,
    classNames = [],
    isOpen: controlledIsOpen,
    onToggle: controlledOnToggle,
}) => {
    const [carryOn, setCarryOn] = useState(defaultCarryOn ?? 1);
    const [hold, setHold] = useState(defaultValue ?? 1);
    const [hasError, setHasError] = useState(false);
    const [internalIsOpen, setInternalIsOpen] = useState(false);
    const dropdownRef = useRef(null);
    const buttonRef = useRef(null);
    const isInitialMount = useRef(true);

    // Use controlled state if provided, otherwise use internal state
    const isOpen = controlledIsOpen !== undefined ? controlledIsOpen : internalIsOpen;

    const bags = parseInt(hold) || 0;
    const carryOnCount = parseInt(carryOn) || 0;
    const totalLuggage = bags + carryOnCount;

    useEffect(() => {
        if (isInitialMount.current) {
            isInitialMount.current = false;
            if (defaultValue !== undefined && defaultValue !== null) {
                setHold(defaultValue);
            }
            if (defaultCarryOn !== undefined && defaultCarryOn !== null) {
                setCarryOn(defaultCarryOn);
            }
        }
    }, [defaultValue, defaultCarryOn]);

    // Update parent whenever carryOn or hold changes.
    // `value` is intentionally omitted from deps: parents typically pass an
    // inline callback (e.g. `value={(t) => updateInputHandler(...)}`) which
    // gets a fresh reference every render. Including it here re-fires the
    // effect every render, which calls value(total) -> parent setState ->
    // new value ref -> loop. The proper fix is for the parent to wrap
    // `value` in useCallback (tracked under Tier 2 of
    // DEPENDENCY_FIX_PLAN.md).
    useEffect(() => {
        if (value && !isInitialMount.current) {
            value({ bags, carryOn: carryOnCount });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hold, carryOn]);

    useEffect(() => {
        if (validateForm) {
            setHasError(totalLuggage < min);
        }
    }, [validateForm, totalLuggage, min]);

    useClickOutside([dropdownRef, buttonRef], () => {
        if (isOpen) {
            if (controlledOnToggle) {
                controlledOnToggle(id, false);
            } else {
                setInternalIsOpen(false);
            }
        }
    });

    /**
     * Clamps a numeric input value between 0 and max.
     * @param {string} inputValue
     * @param {function} setter
     */
    const handleCountChange = (inputValue, setter) => {
        if (inputValue === "") {
            setter(0);
            return;
        }
        const newValue = parseInt(inputValue);
        if (!isNaN(newValue) && newValue >= 0 && newValue <= max) {
            setter(newValue);
            if (onFocus) onFocus();
        }
    };

    /**
     * Increments a count if below max.
     * @param {function} setter
     * @param {number} current
     */
    const handleIncrement = (setter, current) => {
        if (current < max) {
            setter(current + 1);
        }
        if (onFocus) onFocus();
    };

    /**
     * Decrements a count if above 0.
     * @param {function} setter
     * @param {number} current
     */
    const handleDecrement = (setter, current) => {
        if (current > 0) {
            setter(current - 1);
        }
        if (onFocus) onFocus();
    };

    /**
     * Ensures a count stays within bounds on blur.
     * @param {number} count
     * @param {function} setter
     */
    const handleCountBlur = (count, setter) => {
        if (count < 0 || isNaN(count)) {
            setter(0);
        } else if (count > max) {
            setter(max);
        }
    };

    const handleCarryOnIncrement = (e) => {
        e.preventDefault();
        e.stopPropagation();
        handleIncrement(setCarryOn, carryOnCount);
    };

    const handleCarryOnDecrement = (e) => {
        e.preventDefault();
        e.stopPropagation();
        handleDecrement(setCarryOn, carryOnCount);
    };

    const handleCarryOnChange = (e) => {
        e.stopPropagation();
        handleCountChange(e.target.value, setCarryOn);
    };

    const handleCarryOnBlur = () => {
        handleCountBlur(carryOn, setCarryOn);
    };

    const handleHoldIncrement = (e) => {
        e.preventDefault();
        e.stopPropagation();
        handleIncrement(setHold, bags);
    };

    const handleHoldDecrement = (e) => {
        e.preventDefault();
        e.stopPropagation();
        handleDecrement(setHold, bags);
    };

    const handleHoldChange = (e) => {
        e.stopPropagation();
        handleCountChange(e.target.value, setHold);
    };

    const handleHoldBlur = () => {
        handleCountBlur(hold, setHold);
    };

    const toggleDropdown = () => {
        const newIsOpen = !isOpen;
        if (controlledOnToggle) {
            controlledOnToggle(id, newIsOpen);
        } else {
            setInternalIsOpen(newIsOpen);
        }
        if (!isOpen && onFocus) {
            onFocus();
        }
    };

    const classes = getClassName([
        "luggage-dropdown",
        hasError ? "luggage-dropdown--error" : "",
        skeleton ? "luggage-dropdown--skeleton" : "",
        ...classNames,
    ]);

    return (
        <fieldset className={classes}>
            <div className="luggage-dropdown__container">
                <button
                    ref={buttonRef}
                    type="button"
                    aria-label={`Luggage: ${totalLuggage}`}
                    aria-expanded={isOpen}
                    className={getClassName(["luggage-dropdown__button", isOpen && "luggage-dropdown__button--active"])}
                    onClick={toggleDropdown}
                >
                    {icon && <img src={icon} alt="" className="luggage-dropdown__icon" />}
                    <span className="luggage-dropdown__value">{totalLuggage}</span>
                    <img src={isOpen ? chevronUp : chevronDown} alt="" className="luggage-dropdown__chevron" />
                </button>

                {isOpen && (
                    <div ref={dropdownRef} className="luggage-dropdown__popoff">
                        <div className="luggage-dropdown__popoff__tail" />
                        <div className="luggage-dropdown__popoff__content">
                            <div className="luggage-dropdown__popoff__section">
                                <div className="luggage-dropdown__popoff__section__header">
                                    <div className="luggage-dropdown__popoff__section__label">Carry On</div>
                                    <div className="luggage-dropdown__popoff__section__details">22cm X 35cm X 56cm</div>
                                </div>
                                <div className="luggage-dropdown__popoff__section__controls">
                                    <button
                                        type="button"
                                        className="luggage-dropdown__popoff__button luggage-dropdown__popoff__button--minus"
                                        onClick={handleCarryOnDecrement}
                                        disabled={carryOnCount <= 0}
                                    >
                                        −
                                    </button>
                                    <input
                                        type="number"
                                        className="luggage-dropdown__popoff__input"
                                        value={carryOn}
                                        onChange={handleCarryOnChange}
                                        onBlur={handleCarryOnBlur}
                                        min={0}
                                        max={max}
                                    />
                                    <button
                                        type="button"
                                        className="luggage-dropdown__popoff__button luggage-dropdown__popoff__button--plus"
                                        onClick={handleCarryOnIncrement}
                                        disabled={carryOnCount >= max}
                                    >
                                        +
                                    </button>
                                </div>
                            </div>

                            <div className="luggage-dropdown__popoff__section">
                                <div className="luggage-dropdown__popoff__section__header">
                                    <div className="luggage-dropdown__popoff__section__label">Hold</div>
                                    <div className="luggage-dropdown__popoff__section__details">20kg Max each</div>
                                </div>
                                <div className="luggage-dropdown__popoff__section__controls">
                                    <button
                                        type="button"
                                        className="luggage-dropdown__popoff__button luggage-dropdown__popoff__button--minus"
                                        onClick={handleHoldDecrement}
                                        disabled={bags <= 0}
                                    >
                                        −
                                    </button>
                                    <input
                                        type="number"
                                        className="luggage-dropdown__popoff__input"
                                        value={hold}
                                        onChange={handleHoldChange}
                                        onBlur={handleHoldBlur}
                                        min={0}
                                        max={max}
                                    />
                                    <button
                                        type="button"
                                        className="luggage-dropdown__popoff__button luggage-dropdown__popoff__button--plus"
                                        onClick={handleHoldIncrement}
                                        disabled={bags >= max}
                                    >
                                        +
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </fieldset>
    );
};

export default LuggageDropdown;
